import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isBarcodeQuery } from '@/lib/recallUtils';

/**
 * Recherche par code-barres sur TOUTE la base (aucune limite de période ni pagination),
 * dans le champ gtin et dans batch_lot (où RappelConso stocke les GTIN des lots).
 * Renvoie aussi la date du plus ancien rappel en base pour indiquer la période couverte.
 */
export function useBarcodeSearch(rawQuery: string) {
  const code = rawQuery.trim();
  const enabled = isBarcodeQuery(code);

  const results = useQuery({
    queryKey: ['barcode-search', code],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recalls')
        .select('*')
        .or(`gtin.ilike.*${code}*,batch_lot.ilike.*${code}*`)
        .order('published_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const coverage = useQuery({
    queryKey: ['recalls-oldest-date'],
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recalls')
        .select('published_at')
        .order('published_at', { ascending: true })
        .limit(1);
      if (error) throw error;
      return data?.[0]?.published_at ?? null;
    },
  });

  return {
    isBarcode: enabled,
    code,
    recalls: results.data ?? [],
    isLoading: enabled && results.isLoading,
    isError: results.isError,
    oldestDate: coverage.data ?? null,
  };
}
